import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, IconButton, Snackbar, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import { useTranslation } from 'react-i18next';
import TTLLocalStorage from '../../utils/TTLLocalStorage';
import { useDisplayMode } from './useDisplayMode';

const DISMISS_KEY = 'passes_install_dismissed';
const DISMISS_TTL_DAYS = 14;

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const isIosSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(ua);
  const isChromeOrFirefoxIos = /CriOS|FxiOS/.test(ua);
  return isIos && !isChromeOrFirefoxIos;
};

const wasRecentlyDismissed = () => {
  const raw = TTLLocalStorage.getItem(DISMISS_KEY);
  return raw !== null;
};

const markDismissed = () => {
  TTLLocalStorage.setItem(DISMISS_KEY, '1', DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000);
};

export const InstallPrompt = () => {
  const { t } = useTranslation();
  const displayMode = useDisplayMode();
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosOpen, setIosOpen] = useState(false);
  const [suppressed, setSuppressed] = useState<boolean>(wasRecentlyDismissed);

  const showIosBanner = useMemo(
    () => displayMode === 'browser' && !suppressed && isIosSafari(),
    [displayMode, suppressed],
  );

  useEffect(() => {
    if (displayMode !== 'browser') return undefined;
    const handler = (e: Event) => {
      e.preventDefault();
      setPromptEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [displayMode]);

  if (displayMode !== 'browser' || suppressed) return null;

  const handleInstall = async () => {
    if (!promptEvent) return;
    await promptEvent.prompt();
    await promptEvent.userChoice;
    setPromptEvent(null);
  };

  const handleDismiss = () => {
    markDismissed();
    setSuppressed(true);
    setPromptEvent(null);
    setIosOpen(false);
  };

  return (
    <>
      <Snackbar
        open={!!promptEvent}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={t('manage_passes.install.androidPrompt', 'Install the Passes app for quick access')}
        action={
          <>
            <Button color="primary" size="small" onClick={handleInstall}>
              {t('manage_passes.install.installCta', 'Install')}
            </Button>
            <IconButton size="small" color="inherit" onClick={handleDismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      <Snackbar
        open={showIosBanner && !iosOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={t('manage_passes.install.iosPrompt', 'Add Passes to your home screen')}
        action={
          <>
            <Button color="primary" size="small" onClick={() => setIosOpen(true)}>
              {t('manage_passes.install.iosHow', 'How')}
            </Button>
            <IconButton size="small" color="inherit" onClick={handleDismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      <Dialog open={iosOpen} onClose={() => setIosOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('manage_passes.install.iosTitle', 'Add to Home Screen')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {t('manage_passes.install.iosStep1', '1. Tap the Share button in Safari')} <IosShareIcon fontSize="small" />
          </Typography>
          <Typography variant="body1" gutterBottom>
            {t('manage_passes.install.iosStep2', '2. Choose "Add to Home Screen"')}{' '}
            <AddBoxOutlinedIcon fontSize="small" />
          </Typography>
          <Typography variant="body1">{t('manage_passes.install.iosStep3', '3. Tap Add to confirm')}</Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};
